import axios from 'axios'

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true

const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'

const getAuthHeaders = () => {
  const token = localStorage.getItem(JWT_STORAGE_KEY)
  
  // Debug logging
  console.log('[projectsApi.getAuthHeaders] Debug info:', {
    JWT_STORAGE_KEY,
    tokenExists: !!token,
    tokenLength: token ? token.length : 0,
    allLocalStorageKeys: Object.keys(localStorage),
    allTokenLikeKeys: Object.keys(localStorage).filter(k => k.includes('token') || k.includes('auth') || k.includes('jwt'))
  })
  
  if (!token) {
    console.warn('[projectsApi] No JWT token found in localStorage with key:', JWT_STORAGE_KEY)
    console.warn('[projectsApi] Available keys:', Object.keys(localStorage).filter(k => k.includes('token') || k.includes('auth')))
  } else {
    console.debug('[projectsApi] JWT token found (length:', token.length, ')')
    console.debug('[projectsApi] Token preview:', token.substring(0, 50) + '...')
  }
  
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  }
}

export const projectsApi = {
  /**
   * Fetch list of projects
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 50)
   * @returns {Promise<Object>} Projects list response
   */
  async fetchProjects(page = 1, perPage = 50) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/list/`,
        {
          headers: getAuthHeaders(),
          params: {
            page,
            per_page: perPage,
          },
        }
      )
      console.log('[projectsApi] Fetched projects:', response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error fetching projects:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch single project details
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} Project details
   */
  async fetchProject(projectId) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/`,
        {
          headers: getAuthHeaders(),
        }
      )
      return response.data
    } catch (error) {
      console.error('Error fetching project:', error)
      throw error
    }
  },

  /**
   * Create new project with file upload support
   * @param {string} name - Project name (required)
   * @param {string} description - Project description (optional)
   * @param {string} overview - Project overview (optional)
   * @param {File} image - Cover photo file (optional)
   * @returns {Promise<Object>} Created project
   */
  async createProject(name, description = '', overview = '', image = null) {
    try {
      const formData = new FormData()
      formData.append('name', name)
      if (description) formData.append('description', description)
      if (overview) formData.append('overview', overview)
      if (image) formData.append('image', image)

      const token = localStorage.getItem(JWT_STORAGE_KEY)
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/create/`,
        formData,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            // Don't set Content-Type, axios will set it with boundary for FormData
          },
        }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error creating project:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Update project
   * @param {number} projectId - Project ID
   * @param {Object} projectData - Project data to update
   * @returns {Promise<Object>} Updated project
   */
  async updateProject(projectId, projectData) {
    try {
      const response = await axios.patch(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/`,
        projectData,
        {
          headers: getAuthHeaders(),
        }
      )
      return response.data
    } catch (error) {
      console.error('Error updating project:', error)
      throw error
    }
  },

  /**
   * Delete project
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} Response
   */
  async deleteProject(projectId) {
    try {
      const response = await axios.delete(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/`,
        {
          headers: getAuthHeaders(),
        }
      )
      return response.data
    } catch (error) {
      console.error('Error deleting project:', error)
      throw error
    }
  },

  /**
   * Request access to an existing project
   * @param {string} adminEmail - Email of the project admin
   * @param {string} message - Optional message for the admin
   * @returns {Promise<Object>} Request response with admin details
   */
  async requestProjectAccess(adminEmail, message = '') {
    try {
      const formData = new FormData()
      formData.append('admin_email', adminEmail)
      if (message) formData.append('message', message)

      const token = localStorage.getItem(JWT_STORAGE_KEY)
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/request-access/`,
        formData,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            // Don't set Content-Type, axios will set it with boundary for FormData
          },
        }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error requesting project access:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch sites/stations for a specific project
   * @param {number} projectId - Project ID
   * @returns {Promise<Array>} Array of sites with their devices and parameters
   */
  async fetchProjectSites(projectId) {
    try {
      // Fetch from backend API
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/sites/`,
        {
          headers: getAuthHeaders(),
        }
      )
      console.log(`[projectsApi] Fetched sites for project ${projectId}:`, response.data)
      return response.data.sites || response.data || []
    } catch (error) {
      console.error('[projectsApi] Error fetching project sites:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      // Return empty array instead of mock data - let backend data be the source of truth
      return []
    }
  },

  /**
   * Create a new site/station for a project
   * @param {number} projectId - Project ID
   * @param {Object} siteData - Site data
   * @param {string} siteData.name - Site name (required)
   * @param {string} siteData.description - Site description (optional)
   * @param {number} siteData.latitude - Site latitude (optional)
   * @param {number} siteData.longitude - Site longitude (optional)
   * @returns {Promise<Object>} Created site object
   */
  async createSite(projectId, siteData) {
    try {
      console.log('[projectsApi.createSite] Called with projectId:', projectId, 'type:', typeof projectId)
      
      // Backend endpoint: POST /api/project/site/ (ModelViewSet)
      // Django model uses 'project' field (ForeignKey), not 'project_id'
      const formData = new FormData()
      formData.append('name', siteData.name)
      // Send as 'project' (the actual field name in the Django model)
      formData.append('project', String(projectId))
      if (siteData.description) formData.append('description', siteData.description)
      
      // Optional coordinates - use database column names
      if (siteData.latitude) formData.append('sample_pt_latitude', parseFloat(siteData.latitude))
      if (siteData.longitude) formData.append('sample_pt_longitude', parseFloat(siteData.longitude))
      
      // Required fields with sensible defaults (use lowercase field names as per Django model)
      formData.append('status', 'active')      // Default status
      formData.append('demo', 0)               // Not a demo site
      formData.append('deleted', 0)            // Not deleted
      formData.append('display_image', 0)      // Don't display image by default
      formData.append('publicportal', 0)       // NOT publicPortal - must be lowercase
      formData.append('publiczoom', 13)        // NOT publicZoom - must be lowercase, default 13
      formData.append('zoom', 19)              // Default map zoom
      formData.append('autogps', 0)            // NOT autoGPS - must be lowercase
      formData.append('public_default', 0)     // Not public default

      const token = localStorage.getItem(JWT_STORAGE_KEY)
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/site/`,
        formData,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            // Don't set Content-Type, axios will set it with boundary for FormData
          },
        }
      )
      
      console.log(`[projectsApi] Created site for project ${projectId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error creating site:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch the parameter configuration for one device — sensors + their
   * parameters with available unit options + every editable field the
   * Parameter Configuration modal needs.
   *
   * GET /api/data/parameter-config/?device=<id>
   */
  async fetchParameterConfig(deviceId) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/data/parameter-config/`,
        { headers: getAuthHeaders(), params: { device: deviceId }, withCredentials: true }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi.fetchParameterConfig] Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Batch-save parameter configuration changes for a device.
   *
   * POST /api/data/parameter-config/save/
   *   body: { device_id, sensors: [{id, hidden}], params: [{...}] }
   */
  async saveParameterConfig(payload) {
    try {
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/data/parameter-config/save/`,
        payload,
        { headers: getAuthHeaders(), withCredentials: true }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi.saveParameterConfig] Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch the polar histogram backing a Wind Rose widget.
   *
   * @param {number} directionParamId - Parameter ID for wind direction (degrees)
   * @param {number} speedParamId     - Parameter ID for wind speed (m/s)
   * @param {Object} options          - { fromDate, toDate, timezone }
   * @returns {Promise<Object>} `{ sectors, total_paired_samples, dominant_sector, speed_unit, from_date, to_date }`
   */
  async fetchWindRoseData(directionParamId, speedParamId, options = {}) {
    try {
      const params = {
        direction_param_id: directionParamId,
        speed_param_id: speedParamId,
      }
      if (options.fromDate) params.from_date = options.fromDate
      if (options.toDate)   params.to_date   = options.toDate
      if (options.timezone) params.timezone  = options.timezone

      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/data/wind-rose/`,
        {
          headers: getAuthHeaders(),
          params,
          withCredentials: true,
        }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi.fetchWindRoseData] Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch graph data for a parameter
   * @param {number} parameterId - Parameter ID
   * @param {Object} options - Optional parameters
   * @param {string} options.fromDate - Start date (YYYY-MM-DD HH:MM:SS)
   * @param {string} options.toDate - End date (YYYY-MM-DD HH:MM:SS)
   * @param {string} options.timezone - User timezone
   * @returns {Promise<Object>} Graph data with series, units, and metadata
   */
  async fetchGraphData(parameterId, options = {}) {
    try {
      const params = {
        parameter_id: parameterId,
      }
      
      if (options.fromDate) params.from_date = options.fromDate
      if (options.toDate) params.to_date = options.toDate
      if (options.timezone) params.timezone = options.timezone
      // Server-side aggregation for long ranges: interval=hour|day|week|month, agg=avg|min|max.
      if (options.interval) params.interval = options.interval
      if (options.agg) params.agg = options.agg

      const fullUrl = `${API_URL}${API_BASE_PATH}/data/graph-data/`
      console.log(`[projectsApi.fetchGraphData] Making request:`, {
        url: fullUrl,
        params,
        headers: getAuthHeaders()
      })

      const response = await axios.get(
        fullUrl,
        {
          headers: getAuthHeaders(),
          params,
          withCredentials: true,  // Send cookies with the request
        }
      )
      
      console.log(`[projectsApi.fetchGraphData] Response received for parameter ${parameterId}:`, {
        status: response.status,
        dataLength: response.data?.data?.length,
        hasError: response.data?.error,
        data: response.data
      })
      return response.data
    } catch (error) {
      console.error('[projectsApi.fetchGraphData] Error fetching graph data:', {
        parameterId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        message: error.message,
        url: error.config?.url,
        params: error.config?.params
      })
      throw error
    }
  },

  /**
   * Create a new device for a project
   * @param {number} projectId - Project ID
   * @param {Object} deviceData - Device data
   * @param {string} deviceData.name - Device name (required)
   * @param {string} deviceData.uid - Device UID (optional, auto-generated if not provided)
   * @param {number} deviceData.site_id - Site ID (optional)
   * @returns {Promise<Object>} Created device object
   */
  async createDevice(projectId, deviceData) {
    try {
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/device/create/`,
        deviceData,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Created device for project ${projectId}:`, response.data, 'Status:', response.status)
      
      // Handle different response formats:
      // - 201 Created might have minimal/empty body
      // - Backend might wrap in { device: {} } or return device directly
      // - Ensure we always return something usable
      if (response.data && typeof response.data === 'object') {
        return response.data
      } else if (response.status === 201) {
        // 201 Created with empty body - return minimal object
        console.warn('[projectsApi] 201 Created with empty response body')
        return {
          id: deviceData.id || Math.random(),
          name: deviceData.name,
          status: 'created'
        }
      } else {
        return response.data || {}
      }
    } catch (error) {
      console.error('[projectsApi] Error creating device:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Assign or unassign a device to/from a site
   * @param {number} deviceId - Device ID
   * @param {number|null} siteId - Site ID or null to unassign
   * @returns {Promise<Object>} Updated device object
   */
  async assignDeviceToSite(deviceId, siteId) {
    try {
      const response = await axios.put(
        `${API_URL}${API_BASE_PATH}/project/device/${deviceId}/assign/`,
        { site_id: siteId },
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Assigned device ${deviceId} to site ${siteId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error assigning device:', {
        deviceId,
        siteId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch unassigned devices for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Array>} List of unassigned devices
   */
  async fetchUnassignedDevices(projectId) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/unassigned-devices/`,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Fetched unassigned devices for project ${projectId}:`, response.data)
      return response.data.devices || []
    } catch (error) {
      console.error('[projectsApi] Error fetching unassigned devices:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch dashboards for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Array>} List of dashboards
   */
  async fetchDashboards(projectId) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboards/`,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Fetched dashboards for project ${projectId}:`, response.data)
      return response.data.dashboards || response.data || []
    } catch (error) {
      console.error('[projectsApi] Error fetching dashboards:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Create a new dashboard for a project
   * @param {number} projectId - Project ID
   * @param {Object} dashboardData - Dashboard data
   * @param {string} dashboardData.name - Dashboard name (required)
   * @param {string} dashboardData.template - Dashboard template type (optional: 'blank', 'overview', etc.)
   * @returns {Promise<Object>} Created dashboard
   */
  async createDashboard(projectId, dashboardData) {
    try {
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboard/create/`,
        dashboardData,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Created dashboard for project ${projectId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error creating dashboard:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Archive a dashboard
   * @param {number} dashboardId - Dashboard ID
   * @returns {Promise<Object>} Response
   */
  async archiveDashboard(dashboardId) {
    try {
      const response = await axios.patch(
        `${API_URL}${API_BASE_PATH}/dashboard/${dashboardId}/archive/`,
        {},
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Archived dashboard ${dashboardId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error archiving dashboard:', {
        dashboardId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Reactivate an archived dashboard
   * @param {number} dashboardId - Dashboard ID
   * @returns {Promise<Object>} Response
   */
  async reactivateDashboard(dashboardId) {
    try {
      const response = await axios.patch(
        `${API_URL}${API_BASE_PATH}/dashboard/${dashboardId}/reactivate/`,
        {},
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Reactivated dashboard ${dashboardId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error reactivating dashboard:', {
        dashboardId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Delete a dashboard permanently
   * @param {number} dashboardId - Dashboard ID
   * @returns {Promise<Object>} Response
   */
  async deleteDashboard(dashboardId) {
    try {
      const response = await axios.delete(
        `${API_URL}${API_BASE_PATH}/dashboard/${dashboardId}/`,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Deleted dashboard ${dashboardId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error deleting dashboard:', {
        dashboardId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  // ── Project user management ────────────────────────────────────────────────
  // GET    /api/project/<projectId>/users/
  // POST   /api/project/<projectId>/users/add/         { email, access }
  // DELETE /api/project/<projectId>/users/<accountId>/
  //
  // Mirrors the legacy Symfony changeadmin / addcollaborator /
  // removecollaborator / deleteadmin endpoints. Returns { data: [...] }
  // shaped as { id, name, username, email, access } where access is one
  // of 'Owner' | 'Admin' | 'Collaborator'.

  async listProjectUsers(projectId) {
    const response = await axios.get(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/users/`,
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  async addProjectUser(projectId, { email, access }) {
    const response = await axios.post(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/users/add/`,
      { email, access },
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  async removeProjectUser(projectId, accountId) {
    const response = await axios.delete(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/users/${accountId}/`,
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  // ── Project dashboard archive / restore (project-scoped) ───────────────────
  // Backed by TemplateAssignment.is_enabled. Archive hides the dashboard from
  // the project's nav without deleting the underlying template.

  async archiveProjectDashboard(projectId, templateId) {
    const response = await axios.post(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboard/${templateId}/archive/`,
      {},
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  async restoreProjectDashboard(projectId, templateId) {
    const response = await axios.post(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboard/${templateId}/restore/`,
      {},
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  async listArchivedDashboards(projectId) {
    const response = await axios.get(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboards/archived/`,
      { headers: getAuthHeaders() },
    )
    return response.data
  },
}
